import { useState, useCallback } from 'react';

// Touch devices have no hover, so focus is driven by dragging a finger across
// the diagram and selection by lifting it off. Both views work the same way:
// interactive elements tag themselves with data-person_index or data-trip_index,
// and elementFromPoint reports whichever one is under the finger. That respects
// pointer-events, so it finds the invisible hit targets rather than needing the
// visible marks to be hit exactly.
//
// Pass only the person handlers for a view that has no notion of focusing a
// trip, and animating only where there are animations to wait for.
export default function useTouchFocus({ focused_person = null,
                                        focused_trip = null,
                                        set_focused_person,
                                        set_focused_trip = null,
                                        set_selected_person,
                                        set_selected_trip = null,
                                        animating = null }) {

  const [touchmove_enabled, _set_touchmove_enabled] = useState(true);

  const on_touch_move = useCallback((e) => {
    if (e.touches.length > 1) {
      // Disable touchmove if we're panning or zooming
      _set_touchmove_enabled(false);
      return;
    }
    // If touchmove disabled or currently animating, don't do anything
    if (!touchmove_enabled || animating?.current) {
      return;
    }
    const elem = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    if (!elem) {
      return;
    }
    // Deliberately no else: dragging through a gap between marks keeps the last
    // one focused rather than flickering everything back to unfocused.
    if (set_focused_trip && "trip_index" in elem.dataset) {
      set_focused_trip(parseInt(elem.dataset.trip_index));
    } else if ("person_index" in elem.dataset) {
      set_focused_person(parseInt(elem.dataset.person_index));
    }
  }, [touchmove_enabled, animating, set_focused_trip, set_focused_person]);

  const on_touch_end = useCallback((e) => {
    if (e.touches.length === 0) {
      // If all touch points removed from the screen, re-enable touchmove
      _set_touchmove_enabled(true);

      // Make focused person/trip selected
      if (focused_person !== null) {
        set_selected_person(focused_person);
      } else if (set_selected_trip && focused_trip !== null) {
        set_selected_trip(focused_trip);
      }
    }
  }, [focused_person, focused_trip, set_selected_person, set_selected_trip]);

  return { on_touch_move, on_touch_end };
}
